import * as React from 'react';
import '@ant-design/compatible/assets/index.css';
import {
	Modal,
	Input,
	Alert,
	Select,
	Button,
	Switch,
	Checkbox,
	Form,
	Spin,
	Row,
	Radio,
	Popover,
} from 'antd';
import { connect } from 'react-redux';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { DownCircleOutlined, InfoCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import {
	hasRequiredRole,
	ROLES,
	SUPPLIER_ADMIN_ONLY,
	SUPPLIER_ASSIGNABLE_ROLES,
	SUPPLIER_LOCATION_RESTRICTED_ROLES,
	SUPPLIER_TECH,
} from '../roles/roles';
import UserGroupsFormItem from '../user_group_select/UserGroupsFormItem';
import StockLocationFormFields from '../supplier_role_update_form/StockLocationFormFields';
import EmailComponentAntdV4 from '../email_component/EmailComponentAntdV4';
import SupplierRegionsLocationsTreeSelect from '../regions_tree_select/SupplierRegionsLocationsTreeSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchUserForSupplier } from '../../thunks/users_thunks';
import { SUPPLIER_CONTACTS_CRUD_ACTION_CREATORS } from '../../actions/supplier_contacts_actions';
import OWForm from '../ow_form/OWForm';
import BrandSelection from '../brand_selection/BrandSelection';
import { isInternalTech } from '../../utils/AuthUtils';

const FormItem = Form.Item;

function SupplierUserInviteForm(props) {
	const {
		visible,
		onCancel,
		onSubmit,
		formData,
		createErrors,
		updateErrors,
		locations,
		isLocationChange,
		currentUser,
		isReinvite,
		fetchUser,
		saving,
	} = props;
	const [form] = Form.useForm();

	const { setFieldsValue } = form;

	const [showPassword, setShowPassword] = React.useState(false);
	const isSharedContact = Form.useWatch('isSharedContact', form);

	const [user, setUser] = useState();
	const [loading, setLoading] = useState(false);
	const [hasBrands, setHasBrands] = useState(false);

	const email = useMemo(() => nullSafeGet('contact.email', formData), [formData]);
	const roles = Form.useWatch('roles', form);
	const rolesArray: string[] = Array.isArray(roles) ? roles : roles ? [roles] : [];
	const includesSupplierAdmin = hasRequiredRole(rolesArray, SUPPLIER_ADMIN_ONLY);
	const includesSupplierTech = rolesArray.includes(SUPPLIER_TECH);
	const hasLocationRestrictedRole =
		!includesSupplierAdmin && hasRequiredRole(rolesArray, SUPPLIER_LOCATION_RESTRICTED_ROLES);
	const hasAccessToAllLocations = Form.useWatch('hasAccessToAllLocations', form);
	const byLocationOrBrand = Form.useWatch('byLocationOrBrand', form);

	const toggleAccessType = useMemo(
		() => (value) => {
			form.setFieldsValue({
				locationIds: [],
				brandIds: [],
				byLocationOrBrand: value,
			});
		},
		[]
	);

	const refreshUser = useCallback(() => {
		setLoading(true);
		fetchUser(email)
			.then((user) => {
				const locationOrBrand =
					nullSafeGetOrElse('brandIds', user, []).length > 0 ? 'brand' : 'location';
				const hasAccessToAllLocations =
					nullSafeGetOrElse('hasAccessToAllLocations', user, false) ||
					nullSafeGetOrElse('brandIds.length', user, 0) > 0;
				setUser(user);
				setFieldsValue({
					email: nullSafeGet('contact.email', user),
					nameGiven: nullSafeGet('contact.nameGiven', user),
					nameFamily: nullSafeGet('contact.nameFamily', user),
					roles:
						nullSafeGetOrElse('role', user, []).length > 0
							? nullSafeGet('role', user)
							: ['SUPPLIER_USER'],
					locationIds:
						nullSafeGet('locationIds', user) === undefined ? [] : nullSafeGet('locationIds', user),
					hasAccessToAllLocations: !!user.hasAccessToAllLocations,
					isSharedContact: nullSafeGetOrElse('contact.isSharedContact', user, false),
					readOnlyAccess: nullSafeGetOrElse('contact.readOnlyAccess', user, false),
					brandIds: nullSafeGetOrElse('brandIds', user, []),
					byLocationOrBrand: locationOrBrand,
				});
			})
			.finally(() => setLoading(false));
	}, [email, fetchUser, setFieldsValue]);

	useEffect(() => {
		email && refreshUser();
	}, [email, fetchUser, refreshUser]);

	useEffect(() => {
		setFieldsValue({ supplierFacilityId: nullSafeGet('facility.id', currentUser) });
	}, [currentUser, setFieldsValue]);

	const handleSubmit = useCallback(
		(values) => {
			const submittedRoles: string[] = Array.isArray(values.roles)
				? values.roles
				: values.roles
				? [values.roles]
				: [];
			const submittedIncludesAdmin = hasRequiredRole(submittedRoles, SUPPLIER_ADMIN_ONLY);
			const submittedHasRestrictedRole = hasRequiredRole(
				submittedRoles,
				SUPPLIER_LOCATION_RESTRICTED_ROLES
			);
			const hasAccessToAllLocations =
				submittedIncludesAdmin || !submittedHasRestrictedRole
					? true
					: nullSafeGetOrElse('hasAccessToAllLocations', values, false);
			const brandIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'location'), 'location')
					? []
					: values.brandIds;
			const locationIds =
				hasAccessToAllLocations ||
				compareStrings(nullSafeGetOrElse('byLocationOrBrand', values, 'brand'), 'brand')
					? []
					: values.locationIds;
			const updatedValues = {
				...(user || {}),
				...values,
				hasAccessToAllLocations: hasAccessToAllLocations,
				brandIds: brandIds,
				locationIds: locationIds,
			};

			onSubmit && onSubmit(updatedValues);
		},
		[onSubmit, user]
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title={'Invite New User'}
			okText={'Submit'}
			onCancel={onCancel}
			onOk={form.submit}
			closable={true}
			destroyOnClose={true}
			confirmLoading={saving}
		>
			{loading ? (
				<Spin />
			) : (
				<OWForm
					form={form}
					layout="vertical"
					className="supplierForm"
					preserve={false}
					initialValues={{
						roles: nullSafeGetOrElse('roles', formData, ['SUPPLIER_USER']),
						hasAccessToAllLocations: true,
						byLocationOrBrand: 'location',
					}}
					onFinish={handleSubmit}
					actionCreators={SUPPLIER_CONTACTS_CRUD_ACTION_CREATORS}
				>
					{createErrors.length > 0 ? (
						<FormItem>
							<Alert message={createErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					{updateErrors.length > 0 ? (
						<FormItem>
							<Alert message={updateErrors.join(' ')} type="error" />
						</FormItem>
					) : null}
					<EmailComponentAntdV4 disabled={isReinvite} />
					<FormItem
						label={'Name'}
						className={'flex flex-row'}
						required={true}
						rules={[
							{
								required: true,
								message: 'Please input name.',
							},
						]}
					>
						<div className={'flex flex-row'} style={{ marginBottom: -24 }}>
							<FormItem name={'nameGiven'}>
								<Input
									placeholder="Given name"
									style={{ maxWidth: 200 }}
									disabled={false}
									defaultValue={nullSafeGet('contact.nameGiven', formData)}
								/>
							</FormItem>
							<FormItem name={'nameFamily'}>
								<Input
									defaultValue={nullSafeGet('contact.nameFamily', formData)}
									placeholder="Family name"
									style={{ maxWidth: 200, marginLeft: 8 }}
									disabled={false}
								/>
							</FormItem>
						</div>
					</FormItem>

					<FormItem
						label="Roles"
						name="roles"
						required={true}
						rules={[
							{
								required: true,
								message: 'Please select a role',
							},
						]}
					>
						<Select
							mode="multiple"
							disabled={isLocationChange ? isLocationChange : false}
							style={{ minWidth: 200 }}
						>
							{Array.from(SUPPLIER_ASSIGNABLE_ROLES).map((name, idx) => {
								const entity = ROLES[name];
								return (
									<Select.Option value={name} key={`role-${idx}`}>
										{entity.displayName}
									</Select.Option>
								);
							})}
						</Select>
					</FormItem>
					<UserGroupsFormItem memberEmail={email} />
					{hasLocationRestrictedRole ? (
						<>
							<div className="flex flex-row">
								<FormItem name="hasAccessToAllLocations" valuePropName="checked">
									<Row>
										<Switch
											checked={!!form.getFieldValue('hasAccessToAllLocations')}
											onChange={(checked) =>
												form.setFieldsValue({ hasAccessToAllLocations: !!checked })
											}
										/>
										<label style={{ marginLeft: 8 }}>Select All Locations</label>
									</Row>
								</FormItem>
							</div>
							<FormItem
								name="byLocationOrBrand"
								required={!hasAccessToAllLocations && hasBrands}
								hidden={hasAccessToAllLocations || !hasBrands || !isInternalTech(currentUser)}
								label={'Restrict Access By'}
							>
								<Radio.Group
									onChange={(e) => {
										toggleAccessType(e.target.value);
									}}
									value={form.getFieldValue('byLocationOrBrand')}
								>
									<Radio.Button value="location">Location</Radio.Button>
									<Radio.Button value="brand">Brand</Radio.Button>
								</Radio.Group>
							</FormItem>
							<FormItem
								name="brandIds"
								hidden={
									includesSupplierAdmin ||
									hasAccessToAllLocations ||
									!compareStrings(byLocationOrBrand, 'brand') ||
									!isInternalTech(currentUser)
								}
								required={true}
								label={
									<Row>
										<span>Brands</span>
										<Popover
											placement={'topLeft'}
											content={
												'Selecting a brand will give the user access to all locations in that brand.'
											}
										>
											<InfoCircleOutlined
												style={{ display: 'flex', alignItems: 'center', marginLeft: '8px' }}
											/>
										</Popover>
									</Row>
								}
								rules={[
									{
										type: 'array',
										required:
											!hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'brand'),
										message: 'Please select a brand',
									},
								]}
							>
								<BrandSelection setHasBrands={setHasBrands} />
							</FormItem>
							<FormItem
								name="locationIds"
								hidden={
									includesSupplierAdmin ||
									hasAccessToAllLocations ||
									(isInternalTech(currentUser)
										? !compareStrings(byLocationOrBrand, 'location')
										: false)
								}
								required={true}
								label={'Locations'}
								rules={[
									{
										type: 'array',
										required:
											!hasAccessToAllLocations && compareStrings(byLocationOrBrand, 'location'),
										message: 'Please select a location',
									},
								]}
							>
								<SupplierRegionsLocationsTreeSelect
									mode="multiple"
									disabled={hasAccessToAllLocations}
									filtersSlice={locations}
									filtersValueAccessor={() => form.getFieldValue('locationIds')}
									targetCollectionName={'buyerUsersLocations'}
								/>
							</FormItem>
						</>
					) : null}
					{includesSupplierTech ? (
						<StockLocationFormFields setFieldsValue={setFieldsValue} />
					) : null}

					<div className="flex flex-row items-baseline">
						<FormItem name="isSharedContact" valuePropName="checked">
							<Switch />
						</FormItem>
						<label className="ml-2">Shared Account</label>
					</div>
					{!includesSupplierAdmin && (
						<div className="flex flex-row items-baseline">
							<FormItem name="readOnlyAccess" valuePropName="checked">
								<Switch />
							</FormItem>
							<label className="ml-2">Read Only Access</label>
						</div>
					)}
					<div style={{ marginTop: 12 }}>
						{showPassword || isSharedContact ? (
							<>
								{showPassword ? (
									<FormItem>
										<Button
											style={{ padding: 0 }}
											type={'link'}
											onClick={() => setShowPassword(false)}
										>
											<UpCircleOutlined translate="" />
											<span style={{ textTransform: 'capitalize' }}>Hide Password</span>
										</Button>
									</FormItem>
								) : null}

								<FormItem
									name="password"
									label={
										<span>
											Password <span style={{ color: 'rgba(0,0,0,0.45)' }}>(Optional)</span>
										</span>
									}
									required={false}
									style={{ marginBottom: 0 }}
								>
									<Input type="password" style={{ maxWidth: 200 }} />
								</FormItem>
								<div className="flex flex-row items-center">
									<FormItem name="passwordResetRequired" valuePropName="checked" required={false}>
										<Checkbox>Ask user to change password when they sign in</Checkbox>
									</FormItem>
								</div>
							</>
						) : (
							<FormItem>
								<Button style={{ padding: 0 }} type={'link'} onClick={() => setShowPassword(true)}>
									<DownCircleOutlined translate="" />
									<span style={{ textTransform: 'capitalize' }}>Set password</span>
								</Button>
							</FormItem>
						)}
					</div>
				</OWForm>
			)}
		</Modal>
	);
}

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	createErrors: state.supplier_contacts.createErrors,
	updateErrors: state.supplier_contacts.updateErrors,
	creating: state.supplier_contacts.creating,
	updating: state.supplier_contacts.updating,
	currentUser: state.session.currentUser,

	locations: state.locations,
	formData: ownProps.formData,
});

const mapDispatchToProps = (dispatch) => ({
	fetchUser: (email) => dispatch(fetchUserForSupplier(email)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierUserInviteForm);
